import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useMeetingItemQueryParam } from 'Shared/components/MeetingItemDetailPanel/useMeetingItemQueryParam';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Actions
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';
import { setViewedWorkspaceItem, setViewedWorkspaceItemComments } from 'Shared/actions/todos';

// Selectors
import * as meetingItemSelectors from 'Shared/selectors/MeetingItems.selectors';

// Components
import { WpButton } from 'Wpds/components/button';
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';
import WpIcon from 'Wpds/components/icon/WpIcon';
import ItemDoesntExistError from 'Shared/components/ItemDoesntExistError/ItemDoesntExistError';
import MeetingItemComments from './comments/MeetingItemComments';
import MeetingItemHistoryList from './history/MeetingItemHistoryList';
import MeetingItemDetailPanelInfo from './info/MeetingItemDetailPanelInfo';
import MeetingItemDetailsPanelHeader from './header/MeetingItemDetailsPanelHeader';
import MeetingItemDetailPanelLoadingState from './loading/MeetingItemDetailPanelLoadingState';
import MeetingItemCommentsInput from './comments/MeetingItemCommentsInput';

import './MeetingItemDetailPanel.scss';

type Props = {
  fileUploaderRef?: { current: HTMLInputElement };
  handleClosePanel?: Function;
};

const MeetingItemDetailPanel = ({ handleClosePanel }: Props) => {
  const dispatch = useDispatch();
  const fileUploaderRef = useRef(null);
  const scrollToRef = useRef(null);
  const TAB_COMMENTS = 'comments';
  const TAB_HISTORY = 'history';
  const location = useLocation();

  const [hasError, toggleHasError] = useToggle(false);
  const [isFetching, toggleIsFetching] = useToggle(false);
  const { tr } = useTranslation();

  const itemUuid = location.pathname.match(/\/item\/(.*)\/comments/)[1];

  const [activeTab, setActiveTab] = useState(TAB_COMMENTS);

  // we use query params to hide some details while in meeting mode
  const shouldShowFooter = useMeetingItemQueryParam({ paramKey: 'showFooter', defaultValue: true });
  const shouldShowAllComments = useMeetingItemQueryParam({ paramKey: 'showAllComments', defaultValue: false });

  useEffect(() => {
    toggleIsFetching();
    const params = {
      [QueryParamConstants.Api.DATA_FORMAT]: QueryParamConstants.Api.JSONAPI,
    };
    dispatch(meetingItemActions.fetchSingleMeetingItem(itemUuid, params))
      .then(response => {
        // check to see if response is valid, if not - user either doesn't have
        // permission to the item, item slug is invalid, or item doesn't exist anymore
        if (!response) toggleHasError();

        // Remove New badge if it is in workspace.notifications
        dispatch(setViewedWorkspaceItemComments(itemUuid));
        dispatch(setViewedWorkspaceItem(itemUuid));
      })
      .catch(() => {
        toggleHasError();
      })
      .finally(() => {
        toggleIsFetching();
        setActiveTab(TAB_COMMENTS);
      });
  }, [itemUuid]);

  const meetingItem = useSelector(state =>
    meetingItemSelectors.getMeetingItemByUuid(state, { meetingItemUuid: itemUuid })
  ).get(itemUuid);
  const handleCloseClick = () => {
    handleClosePanel();
  };

  const scrollToCallBack = () => {
    scrollToRef.current?.scrollIntoView();
  };

  const loading = isFetching || !meetingItem;

  const baseClassName = 'MeetingItemDetailPanel';

  return (
    <>
      <div className={baseClassName}>
        {hasError ? (
          <ItemDoesntExistError
            title={tr('perform.meeting_item.detail_panel.error.title')}
            description={tr('perform.meeting_item.detail_panel.error.description')}
            buttonLabel={tr('perform.meeting_item.detail_panel.error.close_button')}
            buttonCallback={() => handleClosePanel()}
          />
        ) : (
          <>
            {loading ? (
              <MeetingItemDetailPanelLoadingState />
            ) : (
              <>
                <div className={`${baseClassName}__header`}>
                  <MeetingItemDetailsPanelHeader
                    meetingItem={meetingItem}
                    handleClosePanel={handleClosePanel}
                    fileUploaderRef={fileUploaderRef}
                  />
                </div>

                <div className={`${baseClassName}__sections`}>
                  <div className={`${baseClassName}__info`} style={{ flexGrow: shouldShowAllComments ? 'unset' : 1 }}>
                    <MeetingItemDetailPanelInfo meetingItem={meetingItem} fileUploaderRef={fileUploaderRef} />
                  </div>
                  <div className={`${baseClassName}__toggle-buttons`}>
                    {activeTab === TAB_COMMENTS && (
                      <WpButton variant="tertiary" size="sm" onClick={() => setActiveTab(TAB_HISTORY)}>
                        <WpIcon name="history" />
                        {tr('perform.meeting_item.detail_panel.toggle.view_history')}
                      </WpButton>
                    )}
                    {activeTab === TAB_HISTORY && (
                      <WpButton variant="tertiary" size="sm" onClick={() => setActiveTab(TAB_COMMENTS)}>
                        <WpIcon name="comment" />
                        {tr('perform.meeting_item.detail_panel.toggle.view_comments')}
                      </WpButton>
                    )}
                  </div>

                  <div
                    className={`${baseClassName}__activity-contents`}
                    style={{ flexGrow: shouldShowAllComments ? 1 : 'unset' }}
                  >
                    {activeTab === TAB_COMMENTS && <MeetingItemComments meetingItem={meetingItem} />}
                    {activeTab === TAB_HISTORY && <MeetingItemHistoryList meetingItem={meetingItem} />}
                  </div>
                  <div ref={scrollToRef} />
                </div>

                {shouldShowFooter && activeTab === TAB_COMMENTS && (
                  <div className={`${baseClassName}__comments-input`}>
                    <MeetingItemCommentsInput meetingItem={meetingItem} scrollToCallback={scrollToCallBack} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <WpBackdrop onClick={handleCloseClick} animation={false} />
    </>
  );
};

export default MeetingItemDetailPanel;
